import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2, Bot, User, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { askAssistant, emailAnalysis } from "@/lib/api";
import { ASSISTANT_SUGGESTIONS } from "@/data/veille";

const sessionId = `sess-${Math.random().toString(36).slice(2)}`;

const inline = (str) =>
  str.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong>
      : <span key={j} dangerouslySetInnerHTML={{ __html: p.replace(/<br\/?>/g, "<br/>") }} />
  );

// Markdown renderer supporting pipe tables + bold + line breaks
const renderText = (text) => {
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const isSep = i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes("-");
    if (line.includes("|") && isSep) {
      const header = line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|")) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
        i += 1;
      }
      out.push(
        <div key={`t${i}`} className="my-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead><tr>{header.map((h, k) => <th key={k} className="border border-navy/15 bg-navy text-white px-2 py-1.5 text-left">{inline(h)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri} className={ri % 2 ? "bg-cream/60" : ""}>{r.map((c, ci) => <td key={ci} className="border border-navy/15 px-2 py-1.5 align-top">{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    const clean = line.replace(/^#+\s*/, "");
    out.push(clean.trim() === "" ? <div key={i} className="h-1.5" /> : <p key={i} className="mb-1">{inline(clean)}</p>);
    i += 1;
  }
  return out;
};

const EmailAnalysis = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Email invalide.");
    setLoading(true);
    try {
      const res = await emailAnalysis(email, question, answer);
      if (res.sent) { toast.success("Analyse envoyée par email !"); setSent(true); }
      else toast.error("Service d'envoi momentanément indisponible.");
    } catch { toast.error("Envoi impossible."); } finally { setLoading(false); }
  };

  if (sent) return <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600"><Check size={13} /> Analyse envoyée par email</p>;

  return (
    <div className="mt-3">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="email-analysis-open"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold hover:gap-2.5 transition-all">
          <Mail size={13} /> Envoyer cette analyse par e-mail
        </button>
      ) : (
        <form onSubmit={send} className="flex items-center gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com"
            data-testid="email-analysis-input"
            className="flex-1 rounded-full border border-navy/20 px-3 py-1.5 text-xs outline-none focus:border-gold" autoFocus />
          <button type="submit" disabled={loading} data-testid="email-analysis-send"
            className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-gold to-gold-light px-3 py-1.5 text-xs font-semibold text-navy disabled:opacity-60">
            {loading ? <Loader2 size={12} className="animate-spin" /> : "Envoyer"}
          </button>
        </form>
      )}
    </div>
  );
};

export const AssistantChat = () => {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Bonjour 👋 Je suis l'Assistant Bridge Partners. Je compare pour vous la fiscalité, la réglementation IA et les aides à l'investissement entre la **France & Outre-mer**, le **Bénin** et la **Côte d'Ivoire**." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (question) => {
    const q = (question ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { answer } = await askAssistant(q, sessionId);
      setMessages((m) => [...m, { role: "bot", text: answer, question: q }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Souci technique. Écrivez-nous : contact@bridgepartners.fr" }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-3xl border border-gold/25 bg-navy/50 backdrop-blur-xl overflow-hidden" data-testid="assistant-chat">
      <div className="flex items-center gap-3 border-b border-white/10 px-6 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gold to-gold-light text-navy"><Sparkles size={18} /></span>
        <div>
          <p className="font-display text-white">Assistant Bridge Partners</p>
          <p className="text-xs text-white/50 font-body">Comparateur fiscal & réglementaire · France · Bénin · Côte d'Ivoire</p>
        </div>
      </div>

      <div className="px-6 pt-4 flex flex-wrap gap-2">
        {ASSISTANT_SUGGESTIONS.map((s, i) => (
          <button key={i} onClick={() => send(s)} disabled={loading} data-testid={`assistant-suggestion-${i}`}
            className="rounded-full border border-gold/40 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-gold/15 hover:text-white transition-colors text-left disabled:opacity-50">
            {s}
          </button>
        ))}
      </div>

      <div ref={scrollRef} data-lenis-prevent className="px-6 py-5 max-h-[460px] overflow-y-auto overscroll-contain space-y-4" data-testid="assistant-messages">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-white/10 text-white" : "bg-gold/20 text-gold"}`}>
                {m.role === "user" ? <User size={15} /> : <Bot size={15} />}
              </span>
              <div className={`rounded-2xl px-4 py-3 text-sm font-body leading-relaxed max-w-[88%] ${m.role === "user" ? "bg-gradient-to-r from-gold to-gold-light text-navy" : "bg-white text-anthracite"}`}>
                {m.role === "bot" ? renderText(m.text) : m.text}
                {m.role === "bot" && m.question && <EmailAnalysis question={m.question} answer={m.text} />}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {loading && (
          <div className="flex gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold/20 text-gold"><Bot size={15} /></span>
            <div className="rounded-2xl bg-white px-4 py-3 text-sm text-anthracite flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Analyse en cours…</div>
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 px-4 py-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Posez votre question…"
          data-testid="assistant-input"
          className="flex-1 bg-white/5 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none border border-white/10 focus:border-gold/50 transition-colors" />
        <button type="submit" disabled={loading} data-testid="assistant-send"
          className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-gold to-gold-light text-navy disabled:opacity-60">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};
